import React, {Dispatch, useState} from 'react';
import {
  Button,
  ButtonWithPopout,
  Horizontal,
  List,
  ListItem,
  MoreIcon,
  PopOut,
  ThemeName,
  TrashIcon,
  Vertical,
} from '@taopix/taopix-design-system';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import classNames from 'classnames';
import {useTranslation} from 'react-i18next';
import {UserInterfaceActions} from '../../Context/UserInterface/UserInterfaceActionTypes';
import {useDispatch} from 'react-redux';
import {ActionType} from 'typesafe-actions';
import {CoreActions} from '@taopix/taopix-core';
import {useUserFeedback} from '../../Context/UserFeedback/UserFeedbackContext';
import {UserFeedbackActions} from '../../Context/UserFeedback/UserFeedbackActionTypes';
import ProcessableStack from '@taopix/taopix-asset-manager/dist/types/Stack/ProcessableStack';
import {StatusCheckerEventMap} from '@taopix/taopix-asset-manager/dist/types/StatusChecker/AbstractStatusChecker';
import AssetHelperService from '../../../Service/Assets/AssetHelperService';

type AssetsMoreOptionsProps = {
  monitor: ProcessableStack<number, StatusCheckerEventMap> | undefined;
  assetHelper: AssetHelperService;
};

export const AssetsMoreOptions = ({assetHelper, monitor, ...props}: AssetsMoreOptionsProps) => {
  const ui = useUiState();
  const dispatch = useDispatch<Dispatch<ActionType<typeof CoreActions>>>();
  const userFeedback = useUserFeedback();
  const {t} = useTranslation(['picturemanager']);
  const [popoutOpen, setPopoutOpen] = useState<boolean>(false);

  /**
   * Remove all assets from the project.
   */
  const onClearAssets = React.useCallback(() => {
    if (!monitor) return;

    ui.dispatch(UserInterfaceActions.setIsBusy(true));
    assetHelper
      .onClearAssets()
      .then(response => {
        // delete the assets from the store
        response.success.forEach(assetID => {
          dispatch(CoreActions.deleteAsset(assetID));
          if (monitor.includes(assetID)) {
            monitor.remove(assetID);
          }
        });

        if (response.failed.length > 0) {
          userFeedback.dispatch(
            UserFeedbackActions.setInfoUserFeedback({
              title: '',
              message: t('*:str_ErrorNotDeleted'),
              showHeader: true,
            })
          );
        }
      })
      .catch(error => {
        userFeedback.dispatch(
          UserFeedbackActions.setErrorUserFeedback({
            title: '',
            message: error.message,
            showHeader: true,
          })
        );
      })
      .finally(() => {
        ui.dispatch(UserInterfaceActions.setIsBusy(false));
      });
  }, [monitor]);

  const popoutClassNames = classNames('flex-col');

  return (
    <>
      <ButtonWithPopout
        buttonProps={{
          id: 'manage-assets-more-button',
          label: t('*:str_LabelManageAssetsOptions', {defaultValue: 'Manage assets options'}),
          buttonStyle: 'distinct',
          startIcon: <MoreIcon />,
          hideLabel: true,
          size: 'medium',
          className: 'ml-sm',
        }}
        popoutProps={{
          id: 'more-assets-popout',
          repositionOnResize: true,
          className: popoutClassNames,
          anchorId: 'manage-assets-more-button',
          anchorOrigin: {
            vertical: Vertical.Bottom,
            horizontal: Horizontal.Right,
          },
          transformOrigin: {
            vertical: Vertical.Top,
            horizontal: Horizontal.Right,
          },
          excludeIdFromClickOutside: 'manage-assets-more-button',
          displayMode: 'positioned',
          contentTheme: ThemeName.Popouts,
        }}
      >
        <List>
          <ListItem itemKey={'listItem-removeall'}>
            <Button
              label={t('picturemanager:str_LabelRemoveAllPictures')}
              buttonStyle={'standard'}
              size={'medium'}
              corners={'square'}
              labelAlignment={'left'}
              startIcon={<TrashIcon />}
              hideLabel={false}
              onClick={event => {
                onClearAssets();
              }}
            />
          </ListItem>
        </List>
      </ButtonWithPopout>
    </>
  );
};
